import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createPreviewActions,
  ShareablePreview,
} from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCreateFxRate } from "@/features/ledger/api/use-create-fx-rate";
import type { FxRateDto } from "@/features/ledger/api/dto";
import { useLedgerTranslation } from "@/features/ledger/i18n/use-ledger-translation";
import {
  createFxRateSchema,
  type FxRateFormValues,
} from "@/features/ledger/schemas/fx-rate.schema";

const EMPTY_VALUES: FxRateFormValues = {
  currencyFrom: "GNF",
  currencyTo: "",
  effectiveAt: "",
  rate: 0,
  source: "",
};

const toPayload = (values: FxRateFormValues): FxRateDto => ({
  currencyFrom: values.currencyFrom,
  currencyTo: values.currencyTo,
  // datetime-local → ISO date-time attendue par le service.
  effectiveAt: values.effectiveAt
    ? new Date(values.effectiveAt).toISOString()
    : undefined,
  rate: values.rate,
  source: values.source.trim(),
});

/**
 * Formulaire d'enregistrement d'un taux de change horodaté
 * (`POST /FxRates/add`, guide §6.4) — réservé à flashpay-superadmin /
 * flashpay-admin. Preview ShareablePreview avant submit (le taux est
 * rappelé en ligne de total, hors montant monétaire).
 */
export function FxRateFormDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useLedgerTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : reconstruit le schéma zod traduit à chaque changement de langue.
  const schema = useMemo(() => createFxRateSchema(), [i18n.language]);

  const create = useCreateFxRate();
  const [preview, setPreview] = useState<FxRateDto | null>(null);

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(toPayload(parsed.data));
    },
  });

  const handleConfirm = () => {
    if (!preview) return;
    create.mutate(preview, {
      onSuccess: () => {
        notifSilence(t("ledger.fxRates.feedback.created"));
        setPreview(null);
        onOpenChange(false);
      },
      onError: (error) => notifSilenceError(error.message),
    });
  };

  const fieldError =
    (name: keyof FxRateFormValues) => (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("ledger.fxRates.create")}</DialogTitle>
          </DialogHeader>
          <form
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field
              name="currencyFrom"
              validators={{ onChange: fieldError("currencyFrom") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="currencyFrom">
                    {t("ledger.fxRates.form.currencyFrom")}
                  </Label>
                  <Input
                    id="currencyFrom"
                    className="uppercase notranslate"
                    maxLength={3}
                    placeholder={t("ledger.fxRates.form.currencyFromPlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) =>
                      field.handleChange(e.target.value.toUpperCase())
                    }
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="currencyTo"
              validators={{ onChange: fieldError("currencyTo") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="currencyTo">
                    {t("ledger.fxRates.form.currencyTo")}
                  </Label>
                  <Input
                    id="currencyTo"
                    className="uppercase notranslate"
                    maxLength={3}
                    placeholder={t("ledger.fxRates.form.currencyToPlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) =>
                      field.handleChange(e.target.value.toUpperCase())
                    }
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="rate"
              validators={{ onChange: fieldError("rate") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="rate">{t("ledger.fxRates.form.rate")}</Label>
                  <Input
                    id="rate"
                    type="number"
                    min={0}
                    step="any"
                    className="notranslate"
                    placeholder={t("ledger.fxRates.form.ratePlaceholder")}
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="effectiveAt"
              validators={{ onChange: fieldError("effectiveAt") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="effectiveAt">
                    {t("ledger.fxRates.form.effectiveAt")}
                  </Label>
                  <Input
                    id="effectiveAt"
                    type="datetime-local"
                    className="notranslate"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="source"
              validators={{ onChange: fieldError("source") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="source">
                    {t("ledger.fxRates.form.source")}
                  </Label>
                  <Input
                    id="source"
                    className="notranslate"
                    placeholder={t("ledger.fxRates.form.sourcePlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <DialogFooter className="sm:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={create.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? <ButtonSpinner /> : null}
                {t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {preview ? (
        <ShareablePreview
          data={preview}
          title={t("ledger.fxRates.preview.createTitle")}
          description={t("ledger.fxRates.preview.createDescription")}
          mode="user"
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen && !create.isPending) setPreview(null);
          }}
          maxHeight="90vh"
          actions={createPreviewActions({
            onConfirm: handleConfirm,
            onCancel: () => setPreview(null),
            t,
            confirmLoading: create.isPending,
            withIndicatorOnConfirm: true,
          })}
        />
      ) : null}
    </>
  );
}
